/**
 * QuickLinksPanel (v79.181 — Feb 2026)
 *
 * Founder direction:
 *   "Solutions should be displayed on the main page under the
 *    'Quick Links' section, but they should be kept minimized."
 *
 * Renders a compact, collapsible panel that surfaces the freshest
 * SOLVED community-forum threads so a locked-out / confused visitor
 * lands on a fix without hunting through /forum first.
 *
 * Behaviour:
 *   • Starts MINIMIZED by default (per founder direction). One-line
 *     summary "N quick fixes from the community · Expand".
 *   • Persists open/closed state in localStorage so returning users
 *     land in their preferred state.
 *   • Silently renders nothing when the backend has no solved threads.
 *   • Anonymous-safe — no auth headers required.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ChevronDown, ChevronUp, MessageSquare, ArrowRight } from "lucide-react";
import { forumApi } from "../lib/forum";

const LS_KEY = "adukoo.quicklinks.expanded";

export default function QuickLinksPanel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(() => {
        try { return localStorage.getItem(LS_KEY) === "1"; }
        catch { return false; }
    });

    useEffect(() => {
        let cancelled = false;
        forumApi.quickSolutions(6)
            .then((r) => { if (!cancelled) setItems(r.items || []); })
            .catch(() => { /* silent — panel hides when empty */ })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, []);

    const toggle = () => {
        setExpanded((v) => {
            const nx = !v;
            try { localStorage.setItem(LS_KEY, nx ? "1" : "0"); } catch { /* ignore */ }
            return nx;
        });
    };

    if (loading || items.length === 0) return null;

    return (
        <section
            data-testid="quick-links-panel"
            aria-label="Quick Links — community solutions"
            className="max-w-7xl mx-auto px-4 sm:px-6 mt-3"
        >
            <div className="rounded-xl bg-white border border-aduku-border shadow-sm">
                <button
                    onClick={toggle}
                    data-testid="quick-links-toggle"
                    aria-expanded={expanded}
                    className="w-full flex items-center gap-2 px-3 sm:px-4 py-2.5 text-left"
                >
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-emerald-500 text-white shrink-0">
                        <CheckCircle2 className="w-4 h-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                        <div className="font-display font-black text-aduku-navy text-sm inline-flex items-center gap-1.5">
                            Quick Links
                            <span className="text-[10px] font-bold uppercase tracking-wider text-aduku-navy/50">
                                · community fixes
                            </span>
                        </div>
                        {!expanded && (
                            <div className="text-[11px] text-aduku-navy/60 truncate">
                                {items.length} recent solution{items.length === 1 ? "" : "s"} — tap to expand
                            </div>
                        )}
                    </div>
                    <span className="text-aduku-navy/60 shrink-0">
                        {expanded
                            ? <ChevronUp className="w-4 h-4" />
                            : <ChevronDown className="w-4 h-4" />}
                    </span>
                </button>

                {expanded && (
                    <div data-testid="quick-links-body" className="border-t border-aduku-border">
                        <ul className="divide-y divide-aduku-border">
                            {items.map((t) => (
                                <li key={t.id}>
                                    <Link
                                        to={`/forum/${t.id}`}
                                        data-testid={`quick-link-${t.id}`}
                                        className="flex items-start gap-2 px-3 sm:px-4 py-2.5 hover:bg-aduku-bg transition-colors"
                                    >
                                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 shrink-0 mt-0.5">
                                            <CheckCircle2 className="w-3.5 h-3.5" />
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-aduku-navy text-[13px] line-clamp-1">
                                                {t.title}
                                            </div>
                                            {t.solution_preview && (
                                                <div className="text-aduku-navy/65 text-[11px] leading-snug line-clamp-2">
                                                    {t.solution_preview}
                                                </div>
                                            )}
                                        </div>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                        <div className="px-3 sm:px-4 py-2 bg-aduku-bg/40 flex items-center justify-between text-[11px] gap-2">
                            <span className="inline-flex items-center gap-1 text-aduku-navy/55">
                                <MessageSquare className="w-3 h-3" /> Anyone can post — sign-in optional
                            </span>
                            <Link
                                to="/forum"
                                data-testid="quick-links-open-forum"
                                className="inline-flex items-center gap-1 font-bold text-aduku-navy hover:underline"
                            >
                                Open forum <ArrowRight className="w-3 h-3" />
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}
